import React, { useEffect } from 'react';
import { Box, CssBaseline, CircularProgress } from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import Account from './components/Account/Account';
import Navbar from './components/Navbar/Navbar';
import Auth from './components/Auth/Auth';
import SupportAssistant from './components/SupportAssistant/SupportAssistant';
import Tales from './components/Tales/Tales';
import Form from './components/Form/Form';
import Groups from './components/Groups/Groups';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { styled } from '@mui/material/styles';
import { Settings } from './components/Settings/Settings';
import theme from './theme';
import history from './history';
import './components/Styles/Styles.css';
import { ThemeProvider as CustomThemeProvider } from './contexts/ThemeContext';
import { useAuth } from './contexts/AuthContext';
import axios from 'axios';
import { AuthProvider } from './contexts/AuthContext';
import TaleDetail from './components/Tales/TaleDetail';
import ErrorBoundary from './components/ErrorBoundary/ErrorBoundary';
import Resources from './components/Resources/Resources';
import Feedback from './components/Feedback/Feedback';
import UseCases from './components/UseCases/UseCases';
import DailyUplift from './components/DailyUplift/DailyUplift';
import Privacy from './components/Legal/Privacy';
import Support from './components/Legal/Support';
import Seo from './components/SEO/Seo';
import Footer from './components/Footer/Footer';
import Landing from './components/Landing/Landing';

const RouteSeo = () => {
    const location = useLocation();
    const seoByPath = {
        '/': {
            title: 'Rejection Tales',
            description: 'You got the no. You don’t have to carry it alone. Share stories, sit with it, talk to Luna, and practice when you’re ready.',
            path: '/'
        },
        '/tales': {
            title: 'Rejection Stories',
            description: 'Read real rejection stories from jobs, school, startups, and personal life to learn and grow.',
            path: '/tales'
        },
        '/groups': {
            title: 'Community Groups',
            description: 'Join supportive groups and discuss rejection experiences with people on a similar path.',
            path: '/groups'
        },
        '/resources': {
            title: 'Recovery Resources',
            description: 'Explore curated resources to rebuild confidence after rejection and plan your next move.',
            path: '/resources'
        },
        '/contact': {
            title: 'Contact Rejection Tales',
            description: 'Reach out to the Rejection Tales team with feedback, suggestions, or partnership opportunities.',
            path: '/contact'
        },
        '/privacy': {
            title: 'Privacy Policy',
            description: 'How Rejection Tales collects, uses, and protects your information.',
            path: '/privacy'
        },
        '/support': {
            title: 'Support',
            description: 'Get help with Rejection Tales accounts, the mobile app, Luna, and privacy requests.',
            path: '/support'
        },
        '/auth': {
            title: 'Login',
            description: 'Sign in to Rejection Tales to unlock full stories and member-only support features.',
            path: '/auth'
        }
    };

    const fallback = {
        title: 'Rejection Tales',
        description: 'A supportive platform to share rejection stories, learn, and move forward.',
        path: location.pathname
    };
    const currentSeo = seoByPath[location.pathname] || fallback;

    return <Seo {...currentSeo} />;
};

const App = () => {
    const DashboardLayoutRoot = styled('div')(({ theme }) => ({
      display: 'flex',
      flexDirection: 'column',
      flex: '1 1 auto',
      maxWidth: '100%',
      width: '100%',
      paddingTop: 64,
      minHeight: 'calc(100vh - 64px)',
      alignItems: 'stretch',
      boxSizing: 'border-box',
      '& > *': {
        width: '100%',
        maxWidth: '100%',
      },
    }));

    const ProtectedRoute = ({ children }) => {
        const { currentUser, isAuthenticated, loading } = useAuth();
        
        if (loading) {
            return <CircularProgress />;
        }
        
        if (!currentUser && !isAuthenticated) {
            return <Navigate to="/auth" />;
        }
        
        return children;
    };

    useEffect(() => {
        // Remove any cached manifest URL
        const links = document.querySelectorAll('link[rel="manifest"]');
        links.forEach(link => link.remove());

        // Add the correct manifest link
        const newLink = document.createElement('link');
        newLink.rel = 'manifest';
        newLink.href = `${window.location.origin}/manifest.json`;
        document.head.appendChild(newLink);
    }, []);

    return (  
        <AuthProvider>
          <ThemeProvider theme={theme}>
            <CssBaseline />
            <Box sx={{ 
              display: 'flex',
              flexDirection: 'column',
              minHeight: '100vh'
            }}>
              <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
                <RouteSeo />
                <Navbar/>          
                <DashboardLayoutRoot>
                  <Routes>
                    <Route 
                      path='/create' 
                      element={
                        <ProtectedRoute>
                          <React.Suspense fallback={<div>Loading Form...</div>}>
                            <ErrorBoundary>
                              <Form />
                            </ErrorBoundary>
                          </React.Suspense>
                        </ProtectedRoute>
                      } 
                    />
                    <Route 
                      path='/auth' 
                      element={
                        <AuthRoute>
                          <Auth />
                        </AuthRoute>
                      }
                    />    
                    <Route 
                      path='/' 
                      element={<HomeRoute />}
                    />
                    <Route 
                      path='/settings' 
                      element={
                        <ProtectedRoute>
                          <Settings />
                        </ProtectedRoute>
                      } /> 
                    <Route 
                      path='/account' 
                      element={
                        <ProtectedRoute>
                          <Account />
                        </ProtectedRoute>
                      } />
                    <Route
                      path='/daily-uplift'
                      element={<DailyUplift />}
                    />
                    <Route 
                      path='/groups' 
                      element={<Groups />} />
                    <Route 
                      path='/tales' 
                      element={<Tales />} />
                    <Route 
                      path="/tales/:id" 
                      element={<TaleDetail />} />
                    <Route 
                      path='/create-account' 
                      element={<Account />}
                    />
                    <Route 
                      path="/manifest.json" 
                      element={
                        <Navigate 
                          to={`${window.location.origin}/manifest.json`} 
                          replace 
                        />
                      } 
                    />
                    <Route path="/form" element={<Form />} />
                    <Route path="/edit" element={<Form />} />
                    <Route path="/resources" element={<Resources />} />
                    <Route path="/contact" element={<Feedback />} />
                    <Route path="/privacy" element={<Privacy />} />
                    <Route path="/support" element={<Support />} />
                    <Route
                      path="/about"
                      element={
                        <PublicOnlyRoute>
                          <Landing />
                        </PublicOnlyRoute>
                      }
                    />
                    <Route
                      path="/use-cases"
                      element={
                        <PublicOnlyRoute>
                          <UseCases />
                        </PublicOnlyRoute>
                      }
                    />
                  </Routes>
                </DashboardLayoutRoot>
                <Footer />
              </BrowserRouter>
            </Box>
          </ThemeProvider>
        </AuthProvider>
    );
}

const AuthRoute = ({ children }) => {
    const { isAuthenticated } = useAuth();
    return !isAuthenticated ? children : <Navigate to="/" />;
};

/** Guests see marketing landing; signed-in users get Luna / app home. */
const HomeRoute = () => {
    const { isAuthenticated } = useAuth();
    return isAuthenticated ? <SupportAssistant /> : <Landing />;
};

const PublicOnlyRoute = ({ children }) => {
    const { isAuthenticated } = useAuth();
    return isAuthenticated ? <Navigate to="/" /> : children;
};

export default App;